export interface CollaboratorIdentity {
  clientId: string;
  displayName: string;
  color: string;
}

export interface CollaboratorSelection {
  filePath?: string;
  roomId: string;
  anchor: number;
  head: number;
}

export interface CollaboratorPresence {
  clientId: string;
  displayName: string;
  color: string;
  selection: CollaboratorSelection | null;
  isLocal: boolean;
}

export function getCollaboratorColor(clientId: string): string {
  const palette = ["#D94841", "#2F7D32", "#1769AA", "#8E5A12", "#8A3FFC", "#C44569"];
  let hash = 0;

  for (let index = 0; index < clientId.length; index += 1) {
    hash = (hash * 31 + clientId.charCodeAt(index)) >>> 0;
  }

  return palette[hash % palette.length] ?? palette[0];
}

export function formatPresenceSummary(collaborators: CollaboratorPresence[]): string {
  if (collaborators.length === 0) {
    return "No collaborators";
  }

  return collaborators
    .map((collaborator) => {
      const suffix = collaborator.selection
        ? `cursor ${collaborator.selection.anchor + 1}`
        : "viewing";

      return `${collaborator.displayName} (${suffix})`;
    })
    .join(", ");
}

export function parseCollaboratorIdentity(value: unknown): CollaboratorIdentity | null {
  if (!value || typeof value !== "object") {
    return null;
  }

  const candidate = value as Partial<CollaboratorIdentity>;
  if (!candidate.clientId || !candidate.displayName || !candidate.color) {
    return null;
  }

  return {
    clientId: candidate.clientId,
    displayName: candidate.displayName,
    color: candidate.color
  };
}

export function parseCollaboratorSelection(
  value: unknown,
  roomId: string
): CollaboratorSelection | null {
  if (!value || typeof value !== "object") {
    return null;
  }

  const candidate = value as Partial<CollaboratorSelection>;
  if (
    candidate.roomId !== roomId ||
    typeof candidate.anchor !== "number" ||
    typeof candidate.head !== "number"
  ) {
    return null;
  }

  return {
    filePath: candidate.filePath,
    roomId,
    anchor: candidate.anchor,
    head: candidate.head
  };
}

export function getCollaboratorCursorOffset(
  selection: CollaboratorSelection | null,
  documentLength: number
): number | null {
  if (!selection) {
    return null;
  }

  return Math.max(0, Math.min(documentLength, selection.head));
}
